import type { AxiosInstance } from 'axios';

import axiosImport from '@api/instant_survey/api';
import { REVIEW_STATUS_NUMERIC } from '@api/surveys/utilities/constants';

const axios = axiosImport as unknown as AxiosInstance;

export interface ResponseQualityCounts {
  excluded: number;
  included: number;
}

export interface ResponseQualityItem {
  ai_summary: string;
  completion_time_seconds: number;
  context: {
    quota_group_ref?: string;
    session_id?: string;
    survey_source?: string;
    survey_source_id?: string;
  };
  created: string;
  id: number;
  job: number;
  overquota: boolean;
  ratings: ResponseRating[];
  review_note: string;
  review_status: 'Approved' | 'Pending' | 'Quarantined';
  reward: number;
  reward_status: string;
  reward_type: string;
  score: string;
  status: string;
  survey: number;
  updated: string;
  weighted_question_count: number;
}

export interface ResponseQualityPage {
  count: number;
  next: null | string;
  previous: null | string;
  results: ResponseQualityItem[];
}

export interface ResponseRating {
  assigned_label: string;
  confidence: number;
  flagged: boolean;
  rater_name: string;
  reason: string;
  score: string;
  status: string;
  type: string;
}

export const readResponseQuality = async ({
  includeDisqualified = false,
  params = {},
  signal,
  surveyId,
}: {
  includeDisqualified?: boolean;
  params?: Record<string, unknown>;
  signal?: AbortSignal;
  surveyId: number | string;
}): Promise<ResponseQualityPage> => {
  const response = await axios({
    method: 'get',
    params: {
      include_disqualified: includeDisqualified,
      include_ratings: true,
      ...params,
    },
    signal,
    url: `/survey/${surveyId}/response/`,
  });
  return response.data;
};

export const readResponseQualityCounts = async ({
  includeDisqualified = false,
  signal,
  surveyId,
}: {
  includeDisqualified?: boolean;
  signal?: AbortSignal;
  surveyId: number | string;
}): Promise<ResponseQualityCounts> => {
  const [includedRes, excludedRes] = await Promise.all([
    axios({
      method: 'get',
      params: {
        include_disqualified: includeDisqualified,
        include_ratings: false,
        limit: 1,
        review_status: REVIEW_STATUS_NUMERIC.Approved,
      },
      signal,
      url: `/survey/${surveyId}/response/`,
    }),
    axios({
      method: 'get',
      params: {
        include_disqualified: includeDisqualified,
        include_ratings: false,
        limit: 1,
        review_status: REVIEW_STATUS_NUMERIC.Quarantined,
      },
      signal,
      url: `/survey/${surveyId}/response/`,
    }),
  ]);
  return {
    excluded: excludedRes.data.count,
    included: includedRes.data.count,
  };
};

export const quarantineResponses = async ({
  quarantine,
  reason,
  responseIds,
  surveyId,
}: {
  quarantine: boolean;
  reason: string;
  responseIds: number[];
  surveyId: number | string;
}): Promise<ResponseQualityItem[]> => {
  const response = await axios({
    data: {
      quarantine,
      reason,
      response_ids: responseIds,
    },
    method: 'post',
    url: `/survey/${surveyId}/response/quarantine/`,
  });
  return response.data;
};
